import { Vue, Component, Watch, Prop } from "vue-property-decorator";
import { cloneDeep, sortBy } from "lodash";
import { ReportsModule } from "@/modules/reports-objective";
import { ActionItem, CurriculumType, Action } from "commonui";
import { Button, Dropdown, Menu, Icon } from "ant-design-vue";
import VersionGroupUnitsSelect from "@/components/version-group-units-select/version-group-units-select.vue";
import { VersionGroupModule } from "@/modules/versiongroup";
import {
	ComponentObjectiveReportService,
	IComponentObjectiveReportService,
	UnitRangeQueryParamsModel
} from "@/services/component-objective-report";
import CommonHeader from "../common-header/common-header.vue";
import { MasteryQuestionReportQueryParamsModel, ObjectiveService, UnitRangeModel } from "@/services/objective-manager";
import { getRouteAccessLevel, pureRichText } from "@/util/func";
import { Levels, LevelsText } from "@/util/constants";
import { AccessLevel, ExportType } from "@/util/enum";

export interface LevelData {
	id: string;
	index: string;
	pureText: string;
	text: string;
}

export interface ComponentData {
	contentId: string;
	contentName: string;
}
@Component({
	components: {
		CommonHeader,
		Action,
		ActionItem,
		VersionGroupUnitsSelect,
		Button,		
		Dropdown,
		Menu,
		MenuItem: Menu.Item,
		Icon
	}
})
export default class MasteryQuestionObjectiveHeaderActions extends Vue {
	@Prop({ default: () => {} }) handleExport!: (event: any) => void;
	@Prop({ default: () => {} }) getReportData!: () => void;
	@Prop({ default: () => {} }) enableExportButton!: boolean;
	selectedContentId: string = "null";
	selectedObjectiveId: string = "null";
	componentObjectiveReportService: IComponentObjectiveReportService = ComponentObjectiveReportService();
	components: ComponentData[] = [];
	public unitObject: UnitRangeModel = {} as UnitRangeModel;
	availableObjectives = [];
	availableLevels: object = [
		{ level: Levels.one, label: LevelsText.one },
		{ level: Levels.two, label: LevelsText.two },
		{ level: Levels.three, label: LevelsText.three }
	];
	selectedLevel: number = 1;
	isComponentDropdownLoading = false;
	isObjectiveFilterDropdownLoading = false;
	public level1Data: LevelData[] = [];
	public level2Data: LevelData[] = [];
	public level3Data: LevelData[] = [];
	objectivesData: LevelData[] = [];
	public accessLevel = AccessLevel;
	
	get vcsLevelUnits() {
		return sortBy(ReportsModule.vcsUnits, "unit");
	}

	setUnitObject(value) {
		this.unitObject = value;
		ReportsModule.setUnitRangeForReport(value);
	}

	setSelectedContentId(value) {
		this.selectedContentId = value;
		ReportsModule.setSelectedContentIdForReport(value);
	}

	setSelectedObjectiveId(value) {
		this.selectedObjectiveId = value;
		ReportsModule.setSelectedObjectiveIdForReport(value);
	}

	setSelectedLevel(value) {
		this.selectedLevel = value;
	}

	get filteredComponents() {
		return this.components;
	}

	setComponents(value) {
		this.components = value;
	}

	@Watch("unitObject")
	getUnitsByRange() {
		this.isComponentDropdownLoading = true;
		const queryParams: UnitRangeQueryParamsModel = {
			gsVersionId: VersionGroupModule.selectedGsVersion,
			lsVersionId: VersionGroupModule.selectedLsVersion
		};
		this.componentObjectiveReportService
			.getComponentsByUnitRange(this.unitObject.startUnitId, this.unitObject.endUnitId, queryParams)
			.then((res) => {
				this.setComponents(res);
				this.isComponentDropdownLoading = false;
			})
			.catch((err) => {
				this.isComponentDropdownLoading = false;
			});
	}

	treeLevelSequence(parentIndex, index) {
		return `${parentIndex}${index}.`;
	}

	formatTreeLevel(array, parentIndex): void {
		array.forEach((p, index) => {
			Object.assign(p, {
				key: p.id,
				title: p.text,
				text: p.text,
				show: false,
				index: this.treeLevelSequence(parentIndex, p.sequence)
			});
			if (p.level === Levels.one) {
				this.level1Data.push({
					id: p.key,
					text: `${this.treeLevelSequence(parentIndex, p.sequence)} ${p.text}`,
					pureText: pureRichText(`${this.treeLevelSequence(parentIndex, p.sequence)} ${p.text}`),
					index: this.treeLevelSequence(parentIndex, p.sequence)
				});
			}
			if (p.level === Levels.two) {
				this.level2Data.push({
					id: p.key,
					text: `${this.treeLevelSequence(parentIndex, p.sequence)} ${p.text}`,
					pureText: pureRichText(`${this.treeLevelSequence(parentIndex, p.sequence)} ${p.text}`),
					index: this.treeLevelSequence(parentIndex, p.sequence)
				});
			}
			if (p.level === Levels.three) {
				this.level3Data.push({
					id: p.key,
					text: `${this.treeLevelSequence(parentIndex, p.sequence)} ${p.text}`,
					pureText: pureRichText(`${this.treeLevelSequence(parentIndex, p.sequence)} ${p.text}`),
					index: this.treeLevelSequence(parentIndex, p.sequence)
				});
			}

			if (p.children.length) {
				this.formatTreeLevel(p.children, p.index);
			}
		});
		this.isObjectiveFilterDropdownLoading = false;
	}

	resetLevelData() {
		this.level1Data = [];
		this.level2Data = [];
		this.level3Data = [];
	}

	@Watch("unitObject")
	@Watch("selectedLevel")
	@Watch("selectedContentId")
	getObjectivesBySelectedLevels() {
		this.isObjectiveFilterDropdownLoading = true;
		this.objectivesData = [];
		const queryParams = {
			versionGroupId:
				VersionGroupModule.selectedGsVersion == "-1"
					? VersionGroupModule.selectedLsVersion
					: VersionGroupModule.selectedGsVersion,
			level: Levels.one,
			startUnitId: this.unitObject.startUnitId,
			endUnitId: this.unitObject.endUnitId,
			withDeepest: true,
			mustDeepest: true,
			toLeaves: true,
			contentId: this.selectedContentId,
			getAll: false
		};
		ObjectiveService.getObjectivesTree(queryParams).then((res: any) => {
			this.availableObjectives = cloneDeep(res);
			this.resetLevelData();
			this.formatTreeLevel(res.children, "");
			if (this.selectedLevel == 1) {
				this.objectivesData = cloneDeep(this.level1Data);
			} else if (this.selectedLevel == 2) {
				this.objectivesData = cloneDeep(this.level2Data);
			} else if (this.selectedLevel == 3) {
				this.objectivesData = cloneDeep(this.level3Data);
			}
		});
	}

	get exportType() {
		return ExportType;
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}
}
